/** @fileoverview Helpers for reading, formatting, and checking app field and cell values. */

import type {
  FabrikComponent,
  FabrikDateFormat,
  FabrikGlobals,
  FabrikStateComponent,
  FabrikValueRefValue,
  FabrikValue,
  FabrikVariable,
  InputSnapshot,
} from '../../types/apps';
import type { FieldValues } from './types';
import { valueForReference } from './valueRefs';

export { fieldsForRecordKey, valueForColumn, valueForReference } from './valueRefs';

/** What a value may read besides the row values: the app's variables and the globals. */
export type ValueContext = {
  variables: FabrikVariable[];
  globals: FabrikGlobals;
  inputs?: InputSnapshot;
  /** Finds the label of a value read from a data source field; values without one show as read. */
  labelOf?: (dataSourceId: string, field: string, value: unknown) => string | undefined;
};

export const safeExternalUrl = (value: string): string | null => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname ? url.href : null;
  } catch {
    return null;
  }
};

/**
 * Returns the value when it is a string, or '' otherwise.
 * @param value any value read from a saved row
 */
export const textOf = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Formats an ISO date and time as a short local date and time, or '' when it is not a valid date.
 * @param value ISO date string read from a saved row
 */
export const formatDateTime = (value: unknown): string => {
  if (typeof value !== 'string' || !value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Matches SAP OData v2 dates such as `/Date(1771459200000)/` or `/Date(1771459200000+0060)/`. */
const ODATA_DATE_PATTERN = /^\/Date\((-?\d+)([+-]\d{4})?\)\/$/;

/** Matches a plain calendar date such as `2026-02-19`, with no time part. */
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MS_PER_MINUTE = 60_000;

/** A parsed date and the time zone its calendar date should be read in. */
export type ParsedDateValue = { date: Date; timeZone: 'UTC' | undefined };

/**
 * Parses a SAP OData, date-only, or ISO date-time value, or returns null when it is not a date.
 * SAP and date-only values are read in UTC so they keep the calendar date the source meant;
 * date-time values are read in the viewer's time zone.
 * @param value raw value from a data row
 */
export const parseDateValue = (value: unknown): ParsedDateValue | null => {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  const odataMatch = ODATA_DATE_PATTERN.exec(value);
  if (odataMatch) {
    const [, milliseconds, offsetMinutes] = odataMatch;
    // The optional offset is the source's time zone in minutes; applying it gives the source's own date.
    const date = new Date(Number(milliseconds) + Number(offsetMinutes ?? 0) * MS_PER_MINUTE);
    return Number.isNaN(date.getTime()) ? null : { date, timeZone: 'UTC' };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return { date, timeZone: ISO_DATE_ONLY_PATTERN.test(value) ? 'UTC' : undefined };
};

/**
 * Formats a date value as a short readable date, or returns '' when it is not a date.
 * @param value raw value from a data row
 */
export const formatDate = (value: unknown): string => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '';
  }
  const { date, timeZone } = parsed;
  return date.toLocaleDateString(undefined, { dateStyle: 'medium', timeZone });
};

/**
 * Turns a table cell value into display text, formatting dates in date columns and SAP date strings anywhere.
 * @param value raw value from a data row
 * @param type the column's declared type
 */
export const formatCellValue = (value: unknown, type: string): string => {
  const text = String(value ?? '');
  const isDate = type === 'date' || ODATA_DATE_PATTERN.test(text);
  // Falls back to the raw text when a date column holds something that is not a date.
  return (isDate && formatDate(text)) || text;
};

/** Average days in a month and a year, for rounding long spans. */
const DAYS_PER_MONTH = 30.44;
const DAYS_PER_YEAR = 365.25;

/**
 * Says a span of time in words: "a month", "3 months", "a year", "5 years".
 * @param count how many units
 * @param unit the unit name in the singular
 */
const spanText = (count: number, unit: 'month' | 'year'): string =>
  count === 1 ? `a ${unit}` : `${count} ${unit}s`;

/**
 * Says how far a day is from today in words: "Today", "Tomorrow", "In 6 days", "Yesterday",
 * "15 days ago". Past 30 days it rounds to months ("In 2 months"), and past a year to years
 * ("In a year", "5 years ago"), as exact counts are hard to read at that distance.
 * @param days whole days from today, negative for the past
 */
export const formatRelativeDays = (days: number): string => {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  const distance = Math.abs(days);
  const months = Math.round(distance / DAYS_PER_MONTH);
  const span =
    distance <= 30
      ? `${distance} days`
      : months < 12
      ? spanText(Math.max(months, 1), 'month')
      : spanText(Math.max(Math.round(distance / DAYS_PER_YEAR), 1), 'year');
  const text = days > 0 ? `In ${span}` : `${span} ago`;
  return `${text[0].toUpperCase()}${text.slice(1)}`;
};

/**
 * Formats a count for a metric: grouped digits below ten thousand ("4,210"), a short form above
 * ("12.9M"), so big numbers stay readable in a card.
 * @param count the number to show
 */
export const formatMetricCount = (count: number): string =>
  Math.abs(count) < 10_000
    ? count.toLocaleString()
    : new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
        count
      );

/**
 * Formats a value that is a whole date (SAP OData or YYYY-MM-DD) the same way lists show dates;
 * any other text stays as it is.
 * @param text the display text of a value
 */
export const formatDisplayValue = (text: string): string =>
  ODATA_DATE_PATTERN.test(text) || ISO_DATE_ONLY_PATTERN.test(text)
    ? formatDate(text) || text
    : text;

const MS_PER_DAY = 86_400_000;

/** A calendar date with no time of day. Month is 0-based like `Date`. */
export type CalendarDate = { year: number; month: number; day: number };

/**
 * Reads a raw value as a calendar date, or returns null when it is missing or not a date.
 * @param value raw value from a data row
 */
export const calendarDateOf = (value: unknown): CalendarDate | null => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }
  const { date, timeZone } = parsed;
  return timeZone === 'UTC'
    ? { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() }
    : { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
};

/**
 * Today's calendar date: from a `YYYY-MM-DD` date when given, such as the globals
 * `currentDate`, else in the viewer's own time zone.
 * @param currentDate today's date as `YYYY-MM-DD`, when known
 */
export const todayCalendarDate = (currentDate?: string): CalendarDate => {
  if (currentDate) {
    const [year, month, day] = currentDate.split('-').map(Number);
    return { year, month: month - 1, day };
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
};

/**
 * Counts calendar days from one date to another; negative when `to` is earlier.
 * @param from start date
 * @param to end date
 */
export const daysBetween = (from: CalendarDate, to: CalendarDate): number =>
  Math.round(
    (Date.UTC(to.year, to.month, to.day) - Date.UTC(from.year, from.month, from.day)) / MS_PER_DAY
  );

/**
 * Looks up a variable's value by id, or '' when the variable is missing.
 * @param field the variable id
 * @param variables the app's variables
 */
const variableValueOf = (field: string, variables: FabrikVariable[]): string =>
  variables.find((variable) => variable.id === field)?.value ?? '';

/**
 * Reads a ref's raw value: a record, dataSource, or globals ref through the row values or the
 * globals, and a variables ref through the app's variables. An item ref never resolves here,
 * since no item is ever in scope, so it gives ''.
 * @param ref the ref to read
 * @param fields the screen's row values, keyed by datasource id
 * @param context the app's variables and the globals
 */
const rawRefValueOf = (
  ref: FabrikValueRefValue,
  fields: FieldValues,
  context: ValueContext
): unknown => {
  const { variables, globals } = context;
  switch (ref.scope) {
    case 'record':
    case 'globals':
      return valueForReference(fields, ref, globals);
    case 'variables':
      return variableValueOf(ref.field, variables);
    case 'inputs':
      return ref.formId
        ? context.inputs?.forms?.[ref.formId]?.[ref.field]
        : context.inputs?.standalone?.[ref.field];
    default:
      return '';
  }
};

/**
 * Gives the time a date value starts from: its exact time with a format, else the UTC midnight
 * of its calendar date. Returns null when the value is not a date.
 * @param value raw date value (ISO, date-only, or SAP OData)
 * @param format the text form to write, when set
 */
const dateStartOf = (value: unknown, format?: FabrikDateFormat): number | null => {
  if (format) {
    return parseDateValue(value)?.date.getTime() ?? null;
  }
  const calendarDate = calendarDateOf(value);
  return calendarDate ? Date.UTC(calendarDate.year, calendarDate.month, calendarDate.day) : null;
};

/**
 * Moves a date value by a whole number of days and writes it as text, or '' when the value is
 * not a date. With no format: a plain ISO date (`YYYY-MM-DD`) of its calendar date, so it reads
 * the same in any locale. With a format, in UTC, to match the server's file names: `day`
 * 2026-10-09, `dayTime` 2026-10-09 1430, `month` 2026-10, `year` 2026.
 * @param value raw date value (ISO, date-only, or SAP OData)
 * @param offsetDays whole days to add, positive or negative
 * @param format the text form to write, when set
 */
export const formattedDateOf = (
  value: unknown,
  offsetDays: number,
  format?: FabrikDateFormat
): string => {
  const start = dateStartOf(value, format);
  if (start === null) {
    return '';
  }
  const moved = new Date(start + offsetDays * MS_PER_DAY);
  if (Number.isNaN(moved.getTime())) {
    return '';
  }
  const iso = moved.toISOString();
  switch (format) {
    case 'dayTime':
      return `${iso.slice(0, 10)} ${iso.slice(11, 13)}${iso.slice(14, 16)}`;
    case 'month':
      return iso.slice(0, 7);
    case 'year':
      return iso.slice(0, 4);
    default:
      return iso.slice(0, 10);
  }
};

/**
 * Resolves a value to its underlying raw value, with no display formatting: a plain string as
 * is, a ref's unformatted value, and a date value's result as a plain ISO date (`YYYY-MM-DD`),
 * or in its `format` when it has one.
 * A `template` or `list` value is resolved on the server only, so it resolves to '' here.
 * @param value the definition value
 * @param fields the screen's row values, keyed by datasource id
 * @param context the app's variables and the globals
 */
const rawValueOf = (value: FabrikValue, fields: FieldValues, context: ValueContext): unknown => {
  if (typeof value === 'string') {
    return value;
  }
  if (value.type === 'ref') {
    return rawRefValueOf(value, fields, context);
  }
  if (value.type === 'template' || value.type === 'list') {
    return '';
  }
  const { from: fromValue, offsetDays = 0, format } = value;
  const from = rawValueOf(fromValue, fields, context);
  if (typeof from !== 'string' || !from) {
    return '';
  }
  return formattedDateOf(from, offsetDays, format);
};

/**
 * Resolves a text or label value to display text: a plain string as is, a variables ref as its
 * text, another ref formatted like a table cell (so SAP dates read as dates), and a date value
 * formatted like `formatDate`, or as its fixed `format` text when it has one. An item ref never resolves here, since no item is ever in scope.
 * A `template` or `list` value is rendered on the server only, so it resolves to '' here.
 * @param value the definition value
 * @param fields the screen's row values, keyed by datasource id
 * @param context the app's variables and the globals
 */
export const valueOf = (value: FabrikValue, fields: FieldValues, context: ValueContext): string => {
  if (typeof value === 'string') {
    return value;
  }
  const raw = rawValueOf(value, fields, context);
  if (value.type === 'ref') {
    if (value.scope === 'variables') {
      return String(raw);
    }
    const label =
      value.scope === 'record'
        ? context.labelOf?.(value.dataSourceId, value.field, raw)
        : undefined;
    return label ?? formatCellValue(raw, 'text');
  }
  if (value.type !== 'date') {
    return '';
  }
  return value.format ? String(raw) : formatDate(raw);
};

/**
 * Resolves each value and joins the non-empty ones with the separator.
 * @param values the definition values
 * @param fields the screen's row values, keyed by datasource id
 * @param separator text placed between two values
 * @param context the app's variables and the globals
 */
export const joinValues = (
  values: FabrikValue[],
  fields: FieldValues,
  separator: string,
  context: ValueContext
): string =>
  values
    .map((value) => valueOf(value, fields, context).trim())
    .filter(Boolean)
    .join(separator);

/** Note shown in the builder when an unsaved app has a checklist, timeline, or action button. */
export const SAVE_APP_NOTE = 'Save the app to use this block';

/** Finds a checklist or timeline by id anywhere in a screen's component tree. */
export const findStateComponent = (
  components: FabrikComponent[],
  componentId: string
): FabrikStateComponent | undefined => {
  for (const component of components) {
    if (
      component.id === componentId &&
      (component.kind === 'checklist' || component.kind === 'timeline')
    )
      return component;
    const children =
      component.kind === 'grid'
        ? component.items.map(({ component: child }) => child)
        : component.kind === 'card'
        ? [component.content]
        : [];
    const found = findStateComponent(children, componentId);
    if (found) return found;
  }
  return undefined;
};
